import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DepositSberpayForm } from './DepositSberpayForm';
import { Button, ModalComponentWrapper } from '@/shared/components';
import { useDepositFormLogic } from '@/shared/lib';
import SberPayIcon from '@/shared/assets/sber-pay-logo-icon.svg?react';
import { type CreateOperationSberpayDepositResponse } from '@/features/deposit-sberpay/api';
import type { DepositModalBaseProps } from '@/shared/types';
import { cn } from '@/shared/utils';
import { useAppSettingsDictionary } from '@/shared/api';

function DepositSberpayModalFooter({
  onSubmit,
  isLoading,
  isContinueButton,
}: {
  onSubmit: () => void;
  isLoading: boolean;
  isContinueButton: boolean;
}) {
  const { t } = useTranslation();

  return (
    <Button
      type="submit"
      variant={isContinueButton ? 'secondary' : 'default'}
      size="lg"
      disabled={isLoading}
      loading={isLoading}
      onClick={onSubmit}
      className={cn(
        'w-full gap-[0.8rem] [&_svg]:h-[2.4rem]',
        !isContinueButton && 'bg-sber-green'
      )}>
      {isContinueButton ? (
        t('depositMethods.continueButton')
      ) : (
        <>
          {t('depositMethods.sberpay.submitButton')}
          <SberPayIcon />
        </>
      )}
    </Button>
  );
}

export function DepositSberpayModal({ onBack, title }: DepositModalBaseProps) {
  const { appSettingsData } = useAppSettingsDictionary();

  const [operationData, setOperationData] =
    useState<CreateOperationSberpayDepositResponse>();
  const {
    form,
    handleFormSubmit,
    currencyOptions,
    minAmount,
    amountPlaceholder,
    commissionData,
    shouldShowCommission,
    isSubmitInProgressWithoutErrors,
    formError,
    isLoading,
    accountsInfoError,
    firstDepositDataError,
    createOperationDepositError,
  } = useDepositFormLogic<CreateOperationSberpayDepositResponse>({
    operationType: 'sberpay',
    onCreateDepositSuccess: () => {
      setOperationData(operationData);
    },
  });

  const submitForm = () => {
    form.handleSubmit(handleFormSubmit)();
  };

  const error =
    formError ||
    firstDepositDataError ||
    accountsInfoError ||
    createOperationDepositError;

  return (
    <ModalComponentWrapper
      title={title}
      onBack={onBack}
      footer={
        <DepositSberpayModalFooter
          onSubmit={submitForm}
          isContinueButton={!operationData}
          isLoading={isSubmitInProgressWithoutErrors}
        />
      }>
      <DepositSberpayForm
        form={form}
        handleFormSubmit={handleFormSubmit}
        currencyOptions={currencyOptions}
        minAmount={minAmount}
        amountPlaceholder={amountPlaceholder}
        commissionData={commissionData}
        shouldShowCommission={shouldShowCommission}
        formError={!isSubmitInProgressWithoutErrors && error}
        isLoading={isLoading}
        footerInfo={
          appSettingsData?.value?.depositWithdrawInformation?.sberPayLimits
        }
      />
    </ModalComponentWrapper>
  );
}
